import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useApi } from "@/core/api";
import {
  aiScriptSpreadsheetSheetListKey,
  aiScriptSpreadsheetSheetQueryOptions,
} from "@/payroll/ai-script/spreadsheet-sheet-query";
import type { IAiSpreadsheetSheet } from "@/payroll/ai-script/types";

interface UseSheetPrefetchParams {
  businessId: string | null;
  runId: string;
  sheets: IAiSpreadsheetSheet[];
  activeSheetId: string | null;
}

export const useSheetPrefetch = ({
  businessId,
  runId,
  sheets,
  activeSheetId,
}: UseSheetPrefetchParams) => {
  const api = useApi();
  const queryClient = useQueryClient();

  // Prefetches have no mounted observer, so explicitly cancel any that still
  // belong to this workbook when the viewer moves to another workbook/unmounts.
  // The query function forwards React Query's AbortSignal to Axios.
  useEffect(
    () => () => {
      if (!businessId) return;

      void queryClient.cancelQueries({
        queryKey: aiScriptSpreadsheetSheetListKey(businessId, runId),
      });
    },
    [businessId, queryClient, runId],
  );

  return useCallback(
    (sheetId: string) => {
      if (!businessId || sheetId === activeSheetId) return;

      const sheet = sheets.find((candidate) => candidate.id === sheetId);
      if (!sheet || sheet.empty) return;

      // React Query reuses fresh cached data, joins an existing in-flight query,
      // and allows unrelated sheet keys to fetch concurrently.
      void queryClient.prefetchQuery(
        aiScriptSpreadsheetSheetQueryOptions(api, businessId, runId, sheetId),
      );
    },
    [activeSheetId, api, businessId, queryClient, runId, sheets],
  );
};
